/**
 * AiSuggestPanel.jsx — Phase 10 (2026-02).
 *
 * Floating dropdown anchored to a ✨ Suggest pill button in the ArtByScene
 * drawer's Scene Props or Set Dressing column header. Requests suggestions
 * from POST /projects/{pid}/art/suggest-props, lets the user add them as
 * chips (freetext by default, or asset-backed when the "Also save to asset
 * library" checkbox is on).
 *
 * Scoped to two columns only per PRD v2 §6.2 + Decision 33. Nothing in this
 * component knows how to render itself in ArtByLocation / ArtByCharacter /
 * ArtRequirements — the caller enforces column scope by only mounting the
 * button conditionally.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
// Phase 13 (2026-07): AssetActionsContext import removed — was previously
// called via useAssetActions() but the return value was unused, and it
// threw when AiSuggestPanel is reused outside Art Dept (which lacks the
// provider). If asset-modal wiring is needed, gate a `useContext()`
// tolerant of a null value inside the component body.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _CATEGORY_MAP = {
  // Phase 3 (2026-02) — three columns now. Both Action Props and Scene
  // Props persist at category=PROP; the Action-vs-Scene split is driven
  // by the `is_action_prop` boolean (Phase 4 becomes nullable-override,
  // Phase 3 keeps the current True/False semantic for compatibility with
  // pre-Phase-4 bucketing in ArtByScene.jsx). action_prop suggestions
  // add is_action_prop=true; scene_prop suggestions omit it (defaults
  // to false).
  action_prop:  { legacyCat: 'PROP',              label: 'Action Props',  assetCategory: 'action_prop',  isActionProp: true  },
  // Phase 5 (2026-02) — Scene Prop suggestions now persist at
  // category='SCENE_PROP' (Phase 2 native semantic) rather than
  // category='PROP' + is_action_prop=false. Phase 4 bucketing routes
  // SCENE_PROP items to the Scene Props column via the category
  // fallback path.
  scene_prop:   { legacyCat: 'SCENE_PROP',        label: 'Scene Props',   assetCategory: 'scene_prop',   isActionProp: false },
  set_dressing: { legacyCat: 'ART_SET_DRESSING',  label: 'Set Dressing',  assetCategory: 'set_dressing', isActionProp: false },
};

export default function AiSuggestPanel({
  open, anchorRect, projectId, token, sceneId, column, existingNames = [],
  onClose, onChipAdded,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [added, setAdded] = useState(new Set());   // names already added
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [addingAll, setAddingAll] = useState(false);
  const [error, setError] = useState(null);
  const [alsoSave, setAlsoSave] = useState(false);
  const shownNamesRef = useRef([]);   // for exclude_names on regenerate

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  // Phase 13 (2026-07) — AiSuggestPanel is now reused outside the Art
  // Dept context tree (Master Breakdown scene detail + SceneExpand),
  // so calling useAssetActions() unconditionally throws. The prior
  // "openAssetModal is unused" call has been removed. If a future
  // caller needs asset-modal wiring, gate it behind an optional
  // useContext(AssetActionsContext) read that tolerates a missing
  // provider.

  const cfg = _CATEGORY_MAP[column] || _CATEGORY_MAP.scene_prop;

  const fetchSuggestions = async (regenerate = false) => {
    setError(null);
    if (regenerate) setRegenerating(true); else setLoading(true);
    try {
      const body = { scene_id: sceneId, column };
      if (regenerate) {
        body.regenerate_hint = 'different from previous suggestions';
        body.exclude_names = shownNamesRef.current.slice(0, 60);
      }
      const r = await authAxios.post(
        `/projects/${projectId}/art/suggest-props`, body,
      );
      const list = r.data?.suggestions || [];
      setSuggestions(list);
      // Track all names we've shown across regenerations for exclude_names.
      const nx = new Set(shownNamesRef.current);
      list.forEach(s => nx.add(s.name));
      shownNamesRef.current = [...nx];
      if (!regenerate) setAdded(new Set());
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'AI suggestion failed. Please retry.');
    } finally {
      setLoading(false); setRegenerating(false);
    }
  };

  useEffect(() => {
    if (open) {
      shownNamesRef.current = [];
      setSuggestions([]); setAdded(new Set()); setError(null);
      fetchSuggestions(false);
    }
     
  }, [open, sceneId, column]);   // eslint-disable-line react-hooks/exhaustive-deps

  const addChip = async (name, reason = null, confidence = null) => {
    try {
      const payload = {
        scene_id: sceneId,
        category: cfg.legacyCat,
        description: name,
        origin: 'ai_suggest',
        ai_confidence: (typeof confidence === 'number') ? confidence : null,
      };
      // Phase 3 (2026-02) — action_prop suggestions carry the manual
      // Action Prop flag so pre-Phase-4 bucketing routes them to the
      // right column immediately (bucketOf in ArtByScene.jsx checks
      // is_action_prop first, then falls back to asset_sub_type). Scene
      // Props and Set Dressing suggestions omit the flag (defaults to
      // false → routes to scene_props / set_dressing by category).
      if (cfg.isActionProp) {
        payload.is_action_prop = true;
      }
      const chipR = await authAxios.post(
        `/projects/${projectId}/breakdown-items`, payload,
      );
      const chip = chipR.data;
      if (alsoSave) {
        // Create asset + link the chip via PATCH asset_id.
        try {
          const assetR = await authAxios.post(
            `/projects/${projectId}/art/assets`,
            {
              name, category: cfg.assetCategory,
              linked_scene_ids: [sceneId],
            },
          );
          const assetId = assetR.data?.id;
          if (assetId && chip?.id) {
            await authAxios.patch(
              `/breakdown-items/${chip.id}`, { asset_id: assetId },
            );
            chip.asset_id = assetId;
          }
        } catch (e) {
          console.error(e);
          toast.error('Asset save failed — chip added without asset link.');
        }
      }
      onChipAdded?.(chip);
      setAdded(prev => new Set(prev).add(name));
      toast.success(`Added "${name}"`);
      return true;
    } catch (e) {
      console.error(e);
      toast.error(`Add failed: ${name}`);
      return false;
    }
  };

  const addAll = async () => {
    const pending = suggestions.filter(s => !added.has(s.name));
    if (pending.length === 0) return;
    if (pending.length > 5) {
      if (!window.confirm(`Add all ${pending.length} suggestions to ${cfg.label}?`)) return;
    }
    setAddingAll(true);
    try {
      for (const s of pending) {
        await addChip(s.name, s.reason, s.confidence);
      }
    } finally { setAddingAll(false); }
  };

  if (!open) return null;

  // Position the panel — floating below the anchor rect, right-aligned.
  const style = anchorRect ? {
    position: 'fixed',
    top: Math.min(anchorRect.bottom + 8, window.innerHeight - 400),
    left: Math.max(8, Math.min(anchorRect.left, window.innerWidth - 400)),
  } : {
    position: 'fixed', top: 100, right: 40,
  };

  return (
    <>
      {/* Click-outside catcher */}
      <div className="fixed inset-0 z-40" onClick={onClose} data-testid="ai-suggest-backdrop" />
      <div
        style={{ ...style, width: 380 }}
        className="z-50 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg shadow-2xl overflow-hidden"
        data-testid="ai-suggest-panel"
      >
        <header className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)] bg-[var(--color-panel)]">
          <Sparkles className="w-3.5 h-3.5 text-[var(--color-accent)]" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-accent)]">
            AI Suggestions for {cfg.label}
          </span>
          <button onClick={onClose} className="ml-auto text-[var(--color-text-dim)] hover:text-white"
            data-testid="ai-suggest-close">
            <X className="w-3.5 h-3.5" />
          </button>
        </header>

        <div className="px-3 py-1.5 text-[10px] text-[var(--color-text-dim)] border-b border-[var(--color-panel)]">
          Based on scene synopsis · existing {cfg.label.toLowerCase()}
          {existingNames.length > 0 ? ` (${existingNames.length})` : ''}
        </div>

        <div className="max-h-[380px] overflow-y-auto">
          {loading && (
            <div className="p-3 space-y-2" data-testid="ai-suggest-loading">
              {[0,1,2].map(i => (
                <div key={i} className="h-12 rounded bg-[var(--color-panel)] animate-pulse" />
              ))}
            </div>
          )}
          {!loading && error && (
            <div className="p-3" data-testid="ai-suggest-error">
              <div className="text-[11px] text-[var(--color-priority-critical)] mb-2">{error}</div>
              <button onClick={() => fetchSuggestions(false)}
                className="text-[10px] px-2 py-1 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
                data-testid="ai-suggest-retry">
                Retry
              </button>
            </div>
          )}
          {!loading && !error && suggestions.length === 0 && (
            <div className="p-4 text-center text-[11px] text-[var(--color-text-dim)]">
              No suggestions yet.
            </div>
          )}
          {!loading && !error && suggestions.map((s, i) => {
            const isAdded = added.has(s.name);
            // Phase 3 (2026-02) — case-insensitive whitespace-normalized
            // dedupe against chips already in this column. LLM is also
            // told to skip these server-side, but this catches the case
            // where the LLM slips one through. Rendered as "Already exists"
            // instead of an Add button.
            const normKey = (str) => String(str || '').toLowerCase().trim().replace(/\s+/g, ' ');
            const existingSet = new Set(existingNames.map(normKey));
            const isDupe = existingSet.has(normKey(s.name));
            return (
              <div key={`${s.name}-${i}`}
                className="flex items-start gap-2 px-3 py-2 border-b border-[var(--color-panel)] last:border-b-0"
                style={isDupe && !isAdded ? { opacity: 0.55 } : undefined}
                data-testid={`ai-suggest-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-bold text-[var(--color-text)]">{s.name}</div>
                  {s.reason && (
                    <div className="text-[10px] italic text-[var(--color-text-dim)] mt-0.5">{s.reason}</div>
                  )}
                </div>
                {isAdded ? (
                  <span className="text-[10px] px-2 py-1 rounded bg-[var(--color-set-dressing)]/15 text-[var(--color-set-dressing)] font-bold whitespace-nowrap"
                    data-testid={`ai-suggest-added-${i}`}>
                    ✓ Added
                  </span>
                ) : isDupe ? (
                  <span className="text-[10px] px-2 py-1 rounded border border-[var(--color-border-strong)] text-[var(--color-text-dim)] whitespace-nowrap"
                    title="A chip with this name already exists on the scene"
                    data-testid={`ai-suggest-dupe-${i}`}>
                    Already exists
                  </span>
                ) : (
                  <button
                    onClick={() => addChip(s.name, s.reason, s.confidence)}
                    className="text-[10px] px-2 py-1 rounded bg-[var(--color-set-dressing)] text-black font-bold whitespace-nowrap hover:bg-[var(--color-set-dressing)]"
                    data-testid={`ai-suggest-add-${i}`}>
                    + Add
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <footer className="flex items-center gap-2 px-3 py-2 border-t border-[var(--color-border)] bg-[var(--color-panel)]">
          <label className="text-[10px] text-[var(--color-text-sec)] flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={alsoSave}
              onChange={(e) => setAlsoSave(e.target.checked)}
              className="accent-[var(--color-accent)]"
              data-testid="ai-suggest-also-save" />
            <span>Also save to asset library</span>
          </label>
          <div className="ml-auto flex gap-1.5">
            <button onClick={() => fetchSuggestions(true)}
              disabled={regenerating || loading}
              className="text-[10px] px-2 py-1 rounded text-[var(--color-text-sec)] hover:text-white disabled:opacity-40 flex items-center gap-1"
              data-testid="ai-suggest-regenerate">
              {regenerating
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
              Regenerate
            </button>
            <button onClick={addAll}
              disabled={addingAll || loading || suggestions.length === 0 || suggestions.every(s => added.has(s.name))}
              className="text-[10px] px-2 py-1 rounded border border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent)]/10 disabled:opacity-40"
              data-testid="ai-suggest-add-all">
              {addingAll ? 'Adding…' : 'Add All →'}
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}
